<script lang="ts">
  /**
   * The install link for the Android app.
   *
   * Shown under three conditions, and each of them removes a way to disappoint somebody:
   *
   *   * the server actually has an APK (`/api/config` says so, by looking at the directory
   *     rather than at a setting) — otherwise the link would 404;
   *   * we are not already inside the app, where offering to install it is nonsense;
   *   * and this is an Android browser, because an APK is not something an iPhone or a laptop
   *     can do anything with.
   *
   * The repository is private, so this cannot be a link to a GitHub release: a friend
   * following that URL gets a login page. The instance serves the file itself.
   */
  import { isAndroidBrowser } from './platform.ts';

  type Props = {
    apk: { version: string; size: number } | null | undefined;
    /** The Wear OS build. Shown on every platform — installing it needs a computer. */
    wearApk?: { version: string; size: number } | null | undefined;
    /** Slightly different wording depending on what it sits under. */
    reason?: string;
  };

  const { apk, wearApk, reason }: Props = $props();

  const show = $derived(apk !== null && apk !== undefined && isAndroidBrowser());
  // Deliberately not gated on `isAndroidBrowser`: a watch build is sideloaded over adb from a
  // computer, so the phone is the one place this link is *not* useful.
  const showWear = $derived(wearApk !== null && wearApk !== undefined);
  const megabytes = $derived(apk === null || apk === undefined ? '' : (apk.size / 1048576).toFixed(1));
</script>

{#if show && apk}
  <a class="install" href="/download/hotspotter.apk">
    <span class="what">Android-App installieren</span>
    <span class="meta">Version {apk.version} · {megabytes} MB</span>
  </a>
  {#if reason !== undefined}
    <p class="why">{reason}</p>
  {/if}
  <p class="why">
    Android fragt beim Öffnen, ob es Apps aus dieser Quelle installieren darf — das ist normal
    für eine App, die nicht aus dem Play Store kommt.
  </p>
{/if}

{#if showWear && wearApk}
  <a class="wear" href="/download/hotspotter-wear.apk">
    Für die Uhr: Version {wearApk.version}
  </a>
  <p class="why">
    Wear OS, per <code>adb install</code> vom Rechner. Auf dem Telefon lässt sie sich nicht
    installieren — sie verlangt eine Uhr, und das ist Absicht.
  </p>
{/if}

<style>
  .install {
    display: grid;
    gap: 0.15rem;
    padding: 0.7rem 1rem;
    border: 1px solid var(--accent);
    border-radius: 0.75rem;
    color: var(--fg);
    text-decoration: none;
    text-align: center;
  }

  .what {
    font-size: 0.9375rem;
  }

  .wear {
    display: block;
    padding: 0.5rem 1rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    color: var(--muted);
    font-size: 0.8125rem;
    text-align: center;
    text-decoration: none;
  }

  .meta,
  .why {
    color: var(--muted);
    font-size: 0.75rem;
  }

  .why {
    margin: 0.35rem 0 0;
    text-align: center;
  }
</style>
